/**
 * Badge presentation config: maps SDK enum values to rendering metadata.
 *
 * Status presets are nested by enum group (Payout, Workspace, etc.), with leaf
 * keys = enum values. Each group satisfies its per-enum mapped type, guaranteeing
 * every enum member has a badge via compile-time assertions; any SDK enum drift
 * will fail the `satisfies` check. Nested shape enables autocomplete:
 *   STATUS_BADGE_PRESETS.Workspace.active
 *
 * Service presets are flat, keyed by enum value (including `'*'` for "All Services"):
 *   SERVICE_BADGE_PRESETS.collectionAccount
 *   SERVICE_BADGE_PRESETS['*']
 *
 * Attribute presets are flat, UI-owned markers for a property of the entity
 * rather than its service or lifecycle status — not backed by an SDK enum:
 *   ATTRIBUTE_BADGE_PRESETS.defaultAccount
 *
 * Since SDK enum values are literal camelCase strings (e.g., WorkspaceStatusEnum.Active = 'active'),
 * hardcoded lookups need no SDK enum import — use literal-string keys directly.
 * Runtime lookups (indexing by a variable holding an enum value) are unaffected:
 * e.g., SERVICE_BADGE_PRESETS[serviceType] remains the correct pattern for dynamic access.
 * Note: non-identifier characters (space, hyphen, leading digit, or '*') require bracket
 * notation; `'*'` cannot use dot notation.
 */
import {
  AbnStatusEnum,
  AgreementContractStatusEnum,
  BankDetailStatusEnum,
  CascadeStatusEnum,
  ClientRepresentationStatusEnum,
  GstStatusEnum,
  InvitationStatusEnum,
  MembershipStatusEnum,
  PayoutStatusEnum,
  SaleStatusEnum,
  ServiceTypeEnum,
  WorkspaceStatusEnum,
} from '@sr-sdks/api-sdk-axios';
import type {
  AttributeBadgeType,
  AttributePreset,
  ServiceBadgeType,
  ServiceMeta,
  StatusBadgeType,
  StatusPreset,
  StatusPresetGroup,
} from './appBadgeTypes';

/* eslint-disable perfectionist/sort-objects -- lint-backlog: group declaration order is the precedence of the unscoped statusBadgeMeta lookup (pending → Sale, not Invitation), and SERVICE_BADGE_PRESETS key order is the Titles Service filter order */
export const STATUS_BADGE_PRESETS = {
  Payout: {
    [PayoutStatusEnum.PayoutFinalised]: {
      variant: 'success',
      children: 'Finalised',
      hasIndicator: true,
    },
    [PayoutStatusEnum.PayoutAuthorised]: {
      variant: 'success',
      children: 'Authorised',
      hasIndicator: true,
    },
    [PayoutStatusEnum.PayoutProcessed]: {
      variant: 'success',
      children: 'Processed',
      hasIndicator: true,
    },
    [PayoutStatusEnum.PayoutInitiated]: {
      variant: 'warning',
      children: 'Initiated',
      hasIndicator: true,
    },
    [PayoutStatusEnum.PayoutPending]: {
      variant: 'warning',
      children: 'Pending',
      hasIndicator: true,
    },
    [PayoutStatusEnum.PayoutBounced]: {
      variant: 'destructive',
      children: 'Bounced',
      hasIndicator: true,
    },
    [PayoutStatusEnum.PayoutFailed]: {
      variant: 'destructive',
      children: 'Failed',
      hasIndicator: true,
    },
  },
  Agreement: {
    [AgreementContractStatusEnum.Active]: {
      variant: 'success',
      children: 'Active',
      hasIndicator: true,
    },
    [AgreementContractStatusEnum.Archive]: {
      variant: 'default',
      children: 'Archive',
      hasIndicator: true,
    },
    [AgreementContractStatusEnum.Expired]: {
      variant: 'destructive',
      children: 'Expired',
      hasIndicator: true,
    },
    [AgreementContractStatusEnum.Terminated]: {
      variant: 'destructive',
      children: 'Terminated',
      hasIndicator: true,
    },
  },
  Sale: {
    [SaleStatusEnum.Collected]: {
      variant: 'success',
      children: 'Collected',
      hasIndicator: true,
    },
    [SaleStatusEnum.Disbursed]: {
      variant: 'default',
      children: 'Disbursed',
      hasIndicator: true,
    },
    [SaleStatusEnum.Pending]: {
      variant: 'default',
      children: 'Pending',
      hasIndicator: true,
    },
  },
  Cascade: {
    [CascadeStatusEnum.Final]: {
      variant: 'success',
      children: 'Final',
      hasIndicator: true,
    },
    [CascadeStatusEnum.Provisional]: {
      variant: 'warning',
      children: 'Provisional',
      hasIndicator: true,
    },
  },
  Workspace: {
    [WorkspaceStatusEnum.Active]: {
      variant: 'success',
      children: 'Active',
      hasIndicator: true,
    },
    [WorkspaceStatusEnum.Created]: {
      variant: 'warning',
      children: 'Created',
      hasIndicator: true,
    },
    [WorkspaceStatusEnum.Invited]: {
      variant: 'warning',
      children: 'Invited',
      hasIndicator: true,
    },
    [WorkspaceStatusEnum.Disabled]: {
      variant: 'default',
      children: 'Disabled',
      hasIndicator: true,
    },
    [WorkspaceStatusEnum.Dormant]: {
      variant: 'default',
      children: 'Dormant',
      hasIndicator: true,
    },
  },
  Membership: {
    [MembershipStatusEnum.CurrentMember]: {
      variant: 'success',
      children: 'Current',
      hasIndicator: true,
    },
    [MembershipStatusEnum.InReview]: {
      variant: 'warning',
      children: 'In Review',
      hasIndicator: true,
    },
    [MembershipStatusEnum.PendingApproval]: {
      variant: 'warning',
      children: 'Pending Approval',
      hasIndicator: true,
    },
    [MembershipStatusEnum.AwaitingUpdate]: {
      variant: 'warning',
      children: 'Update Required',
      hasIndicator: true,
    },
    [MembershipStatusEnum.ApplicationSent]: {
      variant: 'warning',
      children: 'Application Sent',
      hasIndicator: true,
    },
    [MembershipStatusEnum.Rejected]: {
      variant: 'destructive',
      children: 'Rejected',
      hasIndicator: true,
    },
    [MembershipStatusEnum.NonMember]: {
      variant: 'default',
      children: 'Non-Member',
      hasIndicator: true,
    },
    [MembershipStatusEnum.InactiveMember]: {
      variant: 'default',
      children: 'Inactive',
      hasIndicator: true,
    },
    [MembershipStatusEnum.RemovedMember]: {
      variant: 'default',
      children: 'Removed',
      hasIndicator: true,
    },
    [MembershipStatusEnum.ResignedMember]: {
      variant: 'default',
      children: 'Resigned',
      hasIndicator: true,
    },
  },
  Invitation: {
    [InvitationStatusEnum.Accepted]: {
      variant: 'success',
      children: 'Accepted',
      hasIndicator: true,
    },
    [InvitationStatusEnum.Created]: {
      variant: 'warning',
      children: 'Created',
      hasIndicator: true,
    },
    [InvitationStatusEnum.Expired]: {
      variant: 'destructive',
      children: 'Expired',
      hasIndicator: true,
    },
    // A sent-but-not-yet-accepted invitation reads as "Invited" everywhere in
    // the app, so the label diverges from the raw `pending` enum value. Because
    // `pending` also exists in the Sale group, consumers must pass
    // `group="Invitation"` to get this preset.
    [InvitationStatusEnum.Pending]: {
      variant: 'warning',
      children: 'Invited',
      hasIndicator: true,
    },
    [InvitationStatusEnum.Failed]: {
      variant: 'destructive',
      children: 'Failed',
      hasIndicator: true,
    },
  },
  BankDetail: {
    [BankDetailStatusEnum.Confirmed]: {
      variant: 'success',
      children: 'Confirmed',
      hasIndicator: true,
    },
    [BankDetailStatusEnum.Unconfirmed]: {
      variant: 'warning',
      children: 'Unconfirmed',
      hasIndicator: true,
    },
    [BankDetailStatusEnum.Blocked]: {
      variant: 'destructive',
      children: 'Blocked',
      hasIndicator: true,
    },
  },
  ClientRepresentation: {
    [ClientRepresentationStatusEnum.Confirmed]: {
      variant: 'success',
      children: 'Confirmed',
      hasIndicator: true,
    },
    [ClientRepresentationStatusEnum.Unconfirmed]: {
      variant: 'warning',
      children: 'Unconfirmed',
      hasIndicator: true,
    },
    [ClientRepresentationStatusEnum.Declined]: {
      hasIndicator: true,
      variant: 'destructive',
      children: 'Declined',
    },
  },
  ABN: {
    [AbnStatusEnum.Active]: {
      variant: 'success',
      children: 'Active',
      hasIndicator: true,
    },
    [AbnStatusEnum.Cancelled]: {
      variant: 'destructive',
      children: 'Cancelled',
      hasIndicator: true,
    },
  },
  GST: {
    [GstStatusEnum.Cancelled]: {
      variant: 'destructive',
      children: 'Cancelled',
      hasIndicator: true,
    },
    [GstStatusEnum.Registered]: {
      variant: 'success',
      children: 'Registered',
      hasIndicator: true,
    },
    [GstStatusEnum.NotRegistered]: {
      variant: 'default',
      children: 'Not Registered',
      hasIndicator: true,
    },
    [GstStatusEnum.NotApplicable]: {
      variant: 'default',
      children: 'Not Applicable',
      hasIndicator: true,
    },
  },
} as const satisfies StatusPreset;

export const SERVICE_BADGE_PRESETS = {
  [ServiceTypeEnum.Star]: {
    children: 'All Services',
    shortLabel: 'ALL SERVICES',
    iconProps: {
      icon: 'currencyCircleDollar',
      weight: 'fill',
      className: 'text-neutral-500',
    },
  },
  [ServiceTypeEnum.CollectionAccount]: {
    children: 'CAM',
    iconProps: {
      icon: 'currencyCircleDollar',
      weight: 'fill',
      className: 'text-ui-success',
    },
  },
  [ServiceTypeEnum.Disbursements]: {
    children: 'Disbursements',
    shortLabel: 'DAS',
    iconProps: {
      icon: 'currencyCircleDollar',
      weight: 'fill',
      className: 'text-badge-yellow',
    },
  },
  [ServiceTypeEnum.Residuals]: {
    children: 'Residuals',
    shortLabel: 'RES',
    iconProps: {
      icon: 'currencyCircleDollar',
      weight: 'fill',
      className: 'text-ui-warning',
    },
  },
  [ServiceTypeEnum.RoyaltiesWorld]: {
    children: 'Royalties World',
    iconProps: {
      icon: 'currencyCircleDollar',
      weight: 'fill',
      className: 'text-badge-blue',
    },
  },
  [ServiceTypeEnum.RoyaltiesAnz]: {
    children: 'Royalties ANZ',
    shortLabel: 'ROY ANZ',
    iconProps: {
      icon: 'currencyCircleDollar',
      weight: 'fill',
      className: 'text-badge-pink',
    },
  },
} as const satisfies Record<ServiceBadgeType, ServiceMeta>;
/* eslint-enable perfectionist/sort-objects */

export const ATTRIBUTE_BADGE_PRESETS = {
  defaultAccount: {
    children: 'Default Account',
    variant: 'secondary',
  },
} as const satisfies AttributePreset;

type ServicePresetValue = (typeof SERVICE_BADGE_PRESETS)[ServiceBadgeType];
type StatusPresetValue = {
  [
    G in StatusPresetGroup
  ]: (typeof STATUS_BADGE_PRESETS)[G][keyof (typeof STATUS_BADGE_PRESETS)[G]];
}[StatusPresetGroup];
type AttributePresetValue =
  (typeof ATTRIBUTE_BADGE_PRESETS)[AttributeBadgeType];

/** Union of every preset value across `SERVICE_BADGE_PRESETS`, `STATUS_BADGE_PRESETS`, and `ATTRIBUTE_BADGE_PRESETS` — the only values {@link AppBadge}'s `type` prop accepts. */
export type AppBadgePreset =
  AttributePresetValue | ServicePresetValue | StatusPresetValue;

/**
 * True when `value` is an own key in some group of `STATUS_BADGE_PRESETS`.
 * Uses `hasOwnProperty` rather than `in` so inherited `Object.prototype`
 * keys (e.g. `'toString'`) cannot falsely pass.
 */
export const isStatusBadgeType = (value: string): value is StatusBadgeType =>
  Object.values(STATUS_BADGE_PRESETS).some((group) =>
    Object.keys(group).includes(value),
  );

/**
 * Presentation meta for a status value, or `undefined` when no preset covers it.
 *
 * Pass `group` whenever the value's meaning is group-specific — an unscoped
 * lookup takes the first group declaring the key, so `pending` would resolve to
 * the Sale preset even for an invitation.
 */
export const statusBadgeMeta = (
  value: string,
  group?: StatusPresetGroup,
): StatusPresetValue | undefined => {
  if (!value) return undefined;

  const groups = group
    ? [STATUS_BADGE_PRESETS[group]]
    : Object.values(STATUS_BADGE_PRESETS);

  const match = groups.find((candidate) =>
    Object.keys(candidate).includes(value),
  ) as Record<string, StatusPresetValue> | undefined;

  return match?.[value];
};

export const isServiceBadgeType = (value: string): value is ServiceBadgeType =>
  Object.prototype.hasOwnProperty.call(SERVICE_BADGE_PRESETS, value);

/** Maps title/API service types to badge meta. */
export const serviceBadgesFromTypes = (
  serviceTypes: readonly ServiceTypeEnum[],
): ServicePresetValue[] =>
  serviceTypes
    .filter(isServiceBadgeType)
    .map((type) => SERVICE_BADGE_PRESETS[type]);

/** The text `ServiceBadge` renders, so a CSV file and the screen agree. */
export const serviceBadgeLabel = (serviceType: ServiceTypeEnum) =>
  serviceBadgesFromTypes([serviceType])
    .map((meta) => meta.children)
    .join('');
